import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ENDPOINTS } from 'app/core/constants/endpoints';
import { SKIP_AUTH_REFRESH } from 'app/core/interceptors/http-context-tokens';
import { ApiService } from 'app/core/services/api.service';
import {
  UpdateProfileRequest,
  UpdateProfileResponse,
} from 'app/modules/home/models/admin';
import { BaseResponse, CategoryModel } from 'app/shared/models';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { DataStorageService } from 'app/shared/utils/data-storage-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  AdminAuthResponse,
  AdminLoginData,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminRefreshResponse,
  AdminUser,
  TwoFactorConfirmResponse,
  TwoFactorSetupResponse,
  TwoFactorVerifyResponse,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends ErrorHandlerService {
  /**
   * The JWT itself lives in an httpOnly cookie the page can't read. These
   * are non-secret markers: "signed in" and "still owes the 2FA step".
   * LEGACY_TOKEN_KEY is where pre-cookie builds kept the raw token; it is
   * exchanged for a cookie once (see migrateLegacyToken) and then deleted.
   */
  private readonly SIGNED_IN_KEY = 'signed_in';
  private readonly MFA_PENDING_KEY = 'mfa_pending';
  private readonly LEGACY_TOKEN_KEY = 'token';
  private readonly USER_KEY = 'user';

  private currentUserSubject = new BehaviorSubject<AdminUser | null>(
    this.getCurrentUser()
  );
  public currentUser$ = this.currentUserSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(
    this.hasValidToken()
  );
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private dataStorageService: DataStorageService
  ) {
    super();
  }

  login(credentials: AdminLoginRequest): Observable<AdminLoginResponse> {
    return this.apiService
      .post<AdminLoginResponse>(
        ENDPOINTS.ADMIN_AUTH.LOGIN,
        credentials,
        new HttpContext().set(SKIP_AUTH_REFRESH, true)
      )
      .pipe(
        tap((response) => {
          if (response.status.success) {
            this.storeAuthData(response.data);
          }
        })
      );
  }

  logout(): Observable<any> {
    return this.apiService.post(ENDPOINTS.ADMIN_AUTH.LOGOUT, {}).pipe(
      tap(() => {
        this.clearAuthData();
      })
    );
  }

  /**
   * * Set Password for First Time
   * @param token string
   * @param password string
   * @param password_confirmation string
   * @returns Observable<BaseResponse>
   */
  setPassword(
    token: string,
    password: string,
    password_confirmation: string
  ): Observable<BaseResponse> {
    return this.apiService.post<BaseResponse>(
      ENDPOINTS.ADMIN_AUTH.SET_PASSWORD,
      {
        token: token,
        password: password,
        password_confirmation: password_confirmation,
      },
      new HttpContext().set(SKIP_AUTH_REFRESH, true)
    );
  }

  getCategories(): CategoryModel[] {
    const categories = sessionStorage.getItem('categories');
    if (categories) {
      return JSON.parse(categories);
    } else {
      const categories = this.dataStorageService.get('categories');
      return categories ? JSON.parse(categories) : [];
    }
  }

  getCurrentAuthenticatedUser(): Observable<AdminAuthResponse> {
    return this.apiService.get<AdminAuthResponse>(ENDPOINTS.ADMIN_AUTH.ME).pipe(
      tap((response) => {
        if (response.status.success) {
          this.updateCurrentUser(response.data);
        }
      })
    );
  }

  refreshToken(): Observable<AdminRefreshResponse> {
    return this.apiService
      .post<AdminRefreshResponse>(
        ENDPOINTS.ADMIN_AUTH.REFRESH,
        {},
        new HttpContext().set(SKIP_AUTH_REFRESH, true)
      )
      .pipe(
        tap((response) => {
          if (response.status.success) {
            // The new token arrived as a cookie; a legacy one is now superseded.
            this.dataStorageService.remove(this.LEGACY_TOKEN_KEY);
            this.dataStorageService.set(this.SIGNED_IN_KEY, '1');
          }
        })
      );
  }

  /**
   * Pre-cookie builds stored the raw token in localStorage. Swap it for a
   * cookie once (refresh sends it as a bearer header via AuthInterceptor),
   * so already signed-in admins aren't logged out by this release.
   */
  migrateLegacyToken(): Promise<void> {
    if (!this.getLegacyToken()) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.refreshToken().subscribe({
        next: () => resolve(),
        error: (error) => {
          if (error?.status === 401 || error?.status === 403) {
            this.clearAuthData();
          }
          resolve();
        },
      });
    });
  }

  getLegacyToken(): string | null {
    return this.dataStorageService.get(this.LEGACY_TOKEN_KEY);
  }

  // ---- Two-factor authentication -------------------------------------

  setupTwoFactor(): Observable<TwoFactorSetupResponse> {
    return this.apiService.post<TwoFactorSetupResponse>(ENDPOINTS.ADMIN_AUTH.TWO_FACTOR_SETUP, {});
  }

  confirmTwoFactor(code: string): Observable<TwoFactorConfirmResponse> {
    return this.apiService
      .post<TwoFactorConfirmResponse>(ENDPOINTS.ADMIN_AUTH.TWO_FACTOR_CONFIRM, { code })
      .pipe(tap((response) => response.status.success && this.markTwoFactorPassed()));
  }

  verifyTwoFactor(payload: { code?: string; recovery_code?: string }): Observable<TwoFactorVerifyResponse> {
    return this.apiService
      .post<TwoFactorVerifyResponse>(ENDPOINTS.ADMIN_AUTH.TWO_FACTOR_VERIFY, payload)
      .pipe(tap((response) => response.status.success && this.markTwoFactorPassed()));
  }

  /** Whether this session still has to pass the 2FA step. */
  isTwoFactorPending(): boolean {
    return this.dataStorageService.get(this.MFA_PENDING_KEY) === '1';
  }

  /** The server rejected a request with mfa_required (e.g. after a role change). */
  markTwoFactorRequired(): void {
    this.dataStorageService.set(this.MFA_PENDING_KEY, '1');
  }

  private markTwoFactorPassed(): void {
    this.dataStorageService.remove(this.MFA_PENDING_KEY);
    this.isAuthenticatedSubject.next(true);
  }

  updateProfile(
    request: UpdateProfileRequest
  ): Observable<UpdateProfileResponse> {
    const formData = new FormData();

    if (request.first_name) {
      formData.append('first_name', request.first_name);
    }
    if (request.last_name) {
      formData.append('last_name', request.last_name);
    }
    if (request.email) {
      formData.append('email', request.email);
    }
    if (request.phone_number) {
      formData.append('phone_number', request.phone_number);
    }
    if (request.avatar) {
      formData.append('avatar', request.avatar);
    }

    return this.apiService
      .post<UpdateProfileResponse>(
        ENDPOINTS.ADMIN_AUTH.UPDATE_PROFILE,
        formData
      )
      .pipe(
        tap((response) => {
          if (response.status.success) {
            this.updateCurrentUser(response.data);
          }
        })
      );
  }

  private storeAuthData(data: AdminLoginData): void {
    this.dataStorageService.remove(this.LEGACY_TOKEN_KEY);
    this.dataStorageService.set(this.SIGNED_IN_KEY, '1');
    if (data.two_factor?.required) {
      this.dataStorageService.set(this.MFA_PENDING_KEY, '1');
    }
    this.dataStorageService.set(this.USER_KEY, JSON.stringify(data.user));
    this.currentUserSubject.next(data.user);
    this.isAuthenticatedSubject.next(!data.two_factor?.required);
  }

  private updateCurrentUser(user: AdminUser): void {
    this.dataStorageService.set(this.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  clearAuthData(): void {
    this.dataStorageService.remove(this.SIGNED_IN_KEY);
    this.dataStorageService.remove(this.MFA_PENDING_KEY);
    this.dataStorageService.remove(this.LEGACY_TOKEN_KEY);
    this.dataStorageService.remove(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }

  clearSession(): void {
    sessionStorage.clear();
    this.dataStorageService.clearAll();
  }


  getCurrentUser(): AdminUser {
    const user = sessionStorage.getItem(this.USER_KEY);
    if (user) {
      return JSON.parse(user);
    } else {
      const user = this.dataStorageService.get(this.USER_KEY);
      return user ? JSON.parse(user) : ({} as AdminUser);
    }
  }

  /** Signed in (a session cookie exists), whether or not 2FA is done yet. */
  hasValidToken(): boolean {
    return this.dataStorageService.get(this.SIGNED_IN_KEY) === '1' || !!this.getLegacyToken();
  }

  /** Signed in and past the 2FA step: allowed into the dashboard. */
  isAuthenticated(): boolean {
    return this.hasValidToken() && !this.isTwoFactorPending();
  }
}
